import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  Suspense,
  lazy,
} from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setAPILoader } from "../../../state/actions/globalAction";
import {
  getLoggedUserInfo,
  getTestModule,
  getUserList,
} from "../../../services/websiteApi";
import {
  getLatestMobilityInsight,
  getRiskAssessmentById,
  getSeniorReport,
  listVitals,
} from "../../../services/v5Api";
import { MOBILITY_REPORT_ENGINE } from "../../../utlis/mobilityReportEngine";
import {
  dateFormatHanlder,
  userInfoFetch,
  formatName,
} from "../../../utlis/commonFunction";
import {
  getParams,
  getSearchParams,
  handleParams,
} from "../../../utlis/helpers";
import {
  ACTIVITY_MONITORING,
  EDIT_TEST_PERSON,
  MOBILITY_REPORT,
  PERSON_DASHBOARD,
  USER_LIST,
  VITALS,
} from "../../../routes/routerPath";
import { buildSessionPath, getStoredSession } from "../../mmtUserList/mmtFlow";
import {
  MAX_MEASUREMENT_SESSION_TESTS,
  getSessionModuleRoute,
  prepareModuleForUpload,
  startMeasurementSession,
} from "../testModule/measurementSession";
import { useMeasurementUploadToasts } from "../testModule/measurementUploadNotifications";
import { getPatientProfile } from "../../../services/dynamicActionPlanApi";
import {
  getPersonTerminology,
  resolveCustomerType,
} from "../../../utlis/personTerminology";
import { isLocalDapPreview } from "./reportQa";
import { getMockActionPlan } from "./dynamicActionPlanV3/data/profiles";
import { DASHBOARD_TABS, getDashboardTabs } from "./dashboardTabs";
import MobilityOverview from "./MobilityOverview";

import {
  getAvailableMeasurementModules,
  isProdUsAccount,
  isStsEligible,
  STS_MODULE_ID,
} from "../../../utlis/measurementModules";
import useProdUsAccount from "../../prodUs/useProdUsAccount";
import ProdUsCreditsModal from "../../prodUs/ProdUsCreditsModal";
import ProdUsDetails from "../../prodUs/ProdUsDetails";
import ProdUsMenu from "../../prodUs/ProdUsMenu";
import {
  CREDITS_PER_CHECK,
  PROD_US_MODULE_IDS,
} from "../../prodUs/prodUsConfig";
import { canStartCheck } from "../../prodUs/prodUsRules";
import { pt } from "../../prodUs/prodUsStrings";
import "../../prodUs/prodUs.scss";
import StsIllustration from "../testModule/sts/StsIllustration";
import { getCurrentLocale } from "../../../utlis/locale";
import { genderLabel } from "../../../utlis/genderLabel";
import { useSection } from "../../../i18n/useSection";
import { useT } from "../../../i18n/useT";
import MoreActionMenu from "../MoreActionMenu";
import InitialsAvatar from "../../plugInElements/InitialsAvatar";
import { RiskQuestionnaire } from "./riskQuestionnaire";
import { MobilityInsights, AssessmentList } from "./mobilityInsights";
const DynamicActionPlan = lazy(
  () => import("./dynamicActionPlanV3/DynamicActionPlan"),
);
import useVitalsListener from "../../../hooks/useVitalsListener";
import FEATURE_FLAGS from "../../../configs/featureFlags";
import { isWebView } from "../../../utlis/webViewBridge";
import { getLatestStoredVitals } from "../../../utlis/vitalsBridge";
import { buildVitalsSubjectInfo } from "../vitals/vitalsSubject";
import "./persondashboard.scss";
import {
  getModuleBadge,
  getModuleDescription,
  getModuleName,
} from "../../../utlis/measurementModuleLabels";

const INSIGHT_DESTINATIONS = [
  {
    id: "mobility",
    titleKey: "mobility_insights",
    descriptionKey: "mobility_insights_desc",
    url: "https://mobility-insights-staging.web.app",
  },
  {
    id: "cognitive",
    titleKey: "cognitive_insights",
    descriptionKey: "cognitive_insights_desc",
    url: "https://cognitive-insights-staging.web.app",
  },
];

// Back-compat: map legacy tab ids (?tab=risk, ?tab=care) to new ones.
const LEGACY_TAB_ID_MAP = { risk: "insights", care: "action" };
const normalizeTabId = (id) => LEGACY_TAB_ID_MAP[id] || id;

const mobilityRiskTone = (riskLevel) => {
  const normalized = String(riskLevel || "")
    .trim()
    .toLowerCase();
  if (normalized === "high") return "high";
  if (["elevated", "moderate", "medium"].includes(normalized)) {
    return "elevated";
  }
  if (normalized === "low") return "low";
  return "normal";
};

const getProfileValue = (profile, fieldNames) => {
  const fields = profile?.data ?? profile;
  const names = new Set(fieldNames);

  if (Array.isArray(fields)) {
    return fields.find((field) => names.has(field?.uniqueId ?? field?.name))
      ?.value;
  }

  for (const name of fieldNames) {
    const value = fields?.[name] ?? profile?.[name];
    if (value !== undefined && value !== null && value !== "") return value;
  }

  return null;
};

const hasValidProfileHeight = (profile) => {
  const height = Number(
    getProfileValue(profile, ["height", "height_cm", "heightCm", "height_in"]),
  );
  return Number.isFinite(height) && height > 0;
};

const resolveActionPlanUserType = (user) => {
  return (
    user?.userType || user?.roleType || user?.role || user?.designation || ""
  );
};

// Senior Living lands on the Daily Roster wrapper; every other tenant renders
// the action plan directly. Tenant aliases mirror dynamicActionPlanV3/data/profiles.js.
const ACTION_PLAN_TENANT_ALIASES = {
  sl: "senior_living",
  seniorliving: "senior_living",
  senior_living: "senior_living",
  fp: "pharmacy",
  familiprix: "pharmacy",
  pharmacy: "pharmacy",
};

const resolveActionPlanTenant = (user) => {
  const raw =
    user?.customer_type ||
    user?.customerType ||
    user?.tenant_type ||
    user?.tenantType ||
    user?.careSetting ||
    user?.tenant ||
    "";
  const normalized = String(raw)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return ACTION_PLAN_TENANT_ALIASES[normalized] || normalized;
};

const resolveActionPlanPatientName = (user) => {
  const params = new URLSearchParams(window.location.search);
  return (
    params.get("dapSeniorName") ||
    params.get("seniorName") ||
    formatName(user, getCurrentLocale()) ||
    user?.name ||
    ""
  );
};

const getSeniorIdFromUser = (user) =>
  user?.id || user?.seniorId || user?.seniorUuid || user?.uuid || "";

// User-list records use `id` for the internal UUID and `seniorId` for the
// displayable SeniorLife ID that Mobility Insights persists as `seniorlife_id`.
const getSeniorLifeIdFromUser = (user) =>
  user?.seniorLifeId ||
  user?.seniorlifeId ||
  user?.seniorlife_id ||
  user?.senior_life_id ||
  user?.seniorId ||
  "";

const buildUserListPathFromCurrentQuery = () => {
  const session = getStoredSession();
  if (session) {
    return buildSessionPath(USER_LIST, session);
  }

  const params = new URLSearchParams(window.location.search);
  [
    "hideNav",
    "moduleId",
    "patient",
    "reportId",
    "seniorId",
    "seniorUuid",
    "tab",
  ].forEach((key) => params.delete(key));

  const query = params.toString();
  return query ? `${USER_LIST}?${query}` : USER_LIST;
};

const PersonDashboard = () => {
  const reportLocale = getCurrentLocale();
  const [rawModuleData, setRawModuleData] = useState([]);
  const [modulesLoadedPerson, setModulesLoadedPerson] = useState(null);
  const selectionPersonRef = useRef(null);
  const modulePersonRef = useRef(null);
  const measurementContextRef = useRef(null);
  const [activeTab, setActiveTab] = useState("insights");
  const [currentUser, setCurrentUser] = useState(() => userInfoFetch() || null);
  const [staffDetails, setStaffDetails] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("staffDetails") || "null");
    } catch {
      return null;
    }
  });
  const [badges, setBadges] = useState({ insights: false, action: false });
  const navTouchStartXRef = useRef(null);
  const navTouchStartYRef = useRef(null);
  const navTouchEndXRef = useRef(null);
  const navTouchEndYRef = useRef(null);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showMobilityInsights, setShowMobilityInsights] = useState(false);
  const [showAssessmentList, setShowAssessmentList] = useState(false);
  const [selectedInsightFrame, setSelectedInsightFrame] = useState(null);
  const [latestMobilityInsight, setLatestMobilityInsight] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [seniorReport, setSeniorReport] = useState(null);
  const [selectedMeasurementIds, setSelectedMeasurementIds] = useState([]);
  const [isCheckingHeight, setIsCheckingHeight] = useState(false);
  const [showHeightPrompt, setShowHeightPrompt] = useState(false);
  const [showVitalsInstructions, setShowVitalsInstructions] = useState(false);
  const [isPreparingVitals, setIsPreparingVitals] = useState(false);
  const [lastCapturedVitals, setLastCapturedVitals] = useState(null);
  // Optimistically show the recent-report card; hide it only once the API
  // confirms there is no report (404 "No test reports found for senior").
  const [hasSeniorReport, setHasSeniorReport] = useState(true);
  const contentRef = useRef(null);
  const suppressReportDeepLinkRef = useRef(false);

  /* eslint-disable no-unused-vars */
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarAnchor, setSnackbarAnchor] = useState({
    vertical: "bottom",
    horizontal: "center",
  });
  const [isResolvingSenior, setIsResolvingSenior] = useState(false);
  /* eslint-enable no-unused-vars */

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const personDashboardStrings = useSection("personDashboard");
  const historyStrings = useSection("history");
  const tDashboard = useT("personDashboard");
  const tSts = useT("sts");
  const {
    seniorId,
    seniorUuid,
    tab: tabFromUrl,
    reportId: reportIdFromUrl,
    hideNav: hideNavParam,
    staffId,
  } = getParams();
  const selectedSeniorId = seniorId || seniorUuid;
  // Hosts (e.g. MMT WebView) can request a chrome-less embed via ?hideNav=true
  // — bottom tab bar is hidden and tab switching is suppressed.
  const isNavHidden = hideNavParam === "true" || hideNavParam === "1";
  const userInfo = currentUser;
  const activeSeniorId = selectedSeniorId || getSeniorIdFromUser(userInfo);
  const mobilityInsightSeniorId =
    getSeniorLifeIdFromUser(userInfo) || activeSeniorId;
  const personLabels = getPersonTerminology(staffDetails || userInfo);
  // Prod_US (SeniorLife Mobility): one person, both tests always, credits, menu instead of back.
  const isProdUs = isProdUsAccount(staffDetails || userInfo);
  const prodUs = useProdUsAccount(isProdUs);
  const prodUsReportVersion = prodUs.reportVersion;
  const prodUsBusy =
    isProdUs && ["uploading", "analyzing"].includes(prodUs.status);
  const prodUsCanStart = canStartCheck(prodUs.credits, CREDITS_PER_CHECK);
  const [prodUsCreditsOpen, setProdUsCreditsOpen] = useState(false);
  const [prodUsMenuOpen, setProdUsMenuOpen] = useState(false);
  const [prodUsDetailsOpen, setProdUsDetailsOpen] = useState(false);
  const moduleData = useMemo(
    () =>
      modulesLoadedPerson === userInfo?.id
        ? getAvailableMeasurementModules(
            rawModuleData,
            staffDetails || userInfo,
          )
        : [],
    [rawModuleData, staffDetails, userInfo, modulesLoadedPerson],
  );
  measurementContextRef.current = {
    personId: activeSeniorId,
    userId: userInfo?.id,
    modules: moduleData,
    selectedIds: selectedMeasurementIds,
  };
  // External person ID (the customer's own ID, e.g. "FPX-104218") shown in the
  // header as in the DAP87 patient page. Never the SeniorLife ID.
  // Pharmacy-only patient page pieces are gated on the account's customer type.
  const isPharmacyTenant =
    resolveCustomerType(staffDetails || userInfo) === "pharmacy";
  const personExternalId = !isPharmacyTenant
    ? ""
    : userInfo?.user_id_from_client ??
      userInfo?.test_person_id ??
      userInfo?.testPersonId ??
      "";
  const dashboardTabs = useMemo(
    () => getDashboardTabs(staffDetails || userInfo),
    [staffDetails, userInfo],
  );
  const loggedUserId = staffId || getStoredSession()?.staffId;

  const getAuthToken = () => {
    const { authToken, accessToken } = getParams();
    if (authToken || accessToken) return authToken || accessToken;
    const storedAccessToken = sessionStorage.getItem("access_token");
    if (storedAccessToken) return storedAccessToken;
    try {
      return (
        JSON.parse(
          sessionStorage.getItem("mmtSession") ||
            localStorage.getItem("mmtSession") ||
            "null",
        )?.accessToken || ""
      );
    } catch {
      return "";
    }
  };

  const buildInsightUrl = (baseUrl) => {
    const url = new URL(baseUrl);
    const authToken = getAuthToken();
    if (authToken) url.searchParams.set("authToken", authToken);
    // The Mobility Insights save endpoint expects the seniors collection UUID
    // here; the backend resolves it to canonical `senior_id` before saving.
    if (activeSeniorId) url.searchParams.set("seniorId", activeSeniorId);
    return url.toString();
  };

  const handleOpenInsight = (insight, sourceUrl = insight.url) => {
    setSelectedInsightFrame({
      ...insight,
      title: personDashboardStrings[insight.titleKey],
      src: buildInsightUrl(sourceUrl),
    });
  };

  const handleOpenLatestMobilityReport = (insight, event) => {
    event?.stopPropagation();
    const reportId = latestMobilityInsight?.reportId;
    if (!reportId) return;

    handleOpenInsight(
      insight,
      new URL(`/r/${encodeURIComponent(reportId)}`, insight.url).toString(),
    );
  };

  const handleCardKeyDown = (event, action) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      action();
    }
  };

  // Handle vitals success
  const handleVitalsSuccess = useCallback((result) => {
    setLastCapturedVitals(
      result?.vitals || result?.response?.data?.vitals || null,
    );
    setSnackbarMessage("Vitals saved successfully!");
    setSnackbarSeverity("success");
    setSnackbarAnchor({ vertical: "bottom", horizontal: "center" });
    setSnackbarOpen(true);
  }, []);

  // Handle vitals error
  const handleVitalsError = useCallback((error) => {
    const statusCode = error?.statusCode || error?.response?.status;
    const exactReason =
      error?.reason ||
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      error?.message;
    const message =
      error?.code === "RESPIRATORY_RATE_BELOW_THRESHOLD"
        ? "The breathing reading was too low to complete this measurement. Please breathe normally and try again."
        : error?.code === "SDK_UNAVAILABLE"
          ? "Vitals require an Inochi build with Welltra VitalsSDK 5.9.3 enabled."
          : exactReason
            ? `${exactReason}${statusCode && !String(exactReason).includes(`HTTP ${statusCode}`) ? ` (HTTP ${statusCode})` : ""}`
            : "Failed to capture or save vitals. Please try again.";
    setSnackbarMessage(message);
    setSnackbarSeverity("error");
    setSnackbarAnchor({ vertical: "bottom", horizontal: "center" });
    setSnackbarOpen(true);
  }, []);

  const handleVitalsCancelled = useCallback((payload) => {
    if (payload?.reason === "preview_dismissed") return;
    setSnackbarMessage("Vitals measurement cancelled. No results were saved.");
    setSnackbarSeverity("info");
    setSnackbarAnchor({ vertical: "bottom", horizontal: "center" });
    setSnackbarOpen(true);
  }, []);

  const showUploadToast = useCallback(({ message, severity }) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity || "success");
    setSnackbarAnchor({ vertical: "top", horizontal: "center" });
    setSnackbarOpen(true);
  }, []);

  useMeasurementUploadToasts(showUploadToast);

  const {
    capability: nativeVitalsCapability,
    isCaptureActive: isVitalsCaptureActive,
    startCapture: startNativeVitalsCapture,
  } = useVitalsListener({
    seniorId: activeSeniorId,
    onSuccess: handleVitalsSuccess,
    onError: handleVitalsError,
    onCancelled: handleVitalsCancelled,
  });

  // Locale strings
  const {
    get_risk_insights,
    exercise_measurement_modules,
    check_vitals,
    activity_analysis,
    mobility_insights_desc,
    view_recent_report,
    view_recent_report_desc,
    check_vitals_desc,
    check_vitals_btn,
    activity_analysis_desc,
    view_alerts_activities,
    last_tested,
    nav_insights,
    nav_metrics,
    nav_monitor,
    nav_action,
    not_available,
    never_tested,
    checking_profile,
    start_session,
    height_required,
    cancel,
    add_height,
  } = personDashboardStrings;
  const bottomNavLabels = {
    nav_insights,
    nav_metrics,
    nav_monitor,
    nav_action,
  };

  // Bottom-nav swipe gesture (only on the nav bar)
  const minSwipeDistance = 40;

  const onNavTouchStart = (e) => {
    navTouchEndXRef.current = null;
    navTouchEndYRef.current = null;
    navTouchStartXRef.current = e.targetTouches[0].clientX;
    navTouchStartYRef.current = e.targetTouches[0].clientY;
  };

  const onNavTouchMove = (e) => {
    navTouchEndXRef.current = e.targetTouches[0].clientX;
    navTouchEndYRef.current = e.targetTouches[0].clientY;
  };

  const onNavTouchEnd = () => {
    const startX = navTouchStartXRef.current;
    const startY = navTouchStartYRef.current;
    const endX = navTouchEndXRef.current;
    const endY = navTouchEndYRef.current;

    if (startX == null || startY == null || endX == null || endY == null) {
      return;
    }

    const dx = startX - endX;
    const dy = startY - endY;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    // Only treat as a swipe if it is clearly horizontal.
    const isHorizontalSwipe =
      absDx > minSwipeDistance && (absDy === 0 || absDx / absDy > 1.5);
    if (!isHorizontalSwipe) return;

    const currentIndex = dashboardTabs.findIndex((tab) => tab.id === activeTab);
    const isLeftSwipe = dx > minSwipeDistance;
    const isRightSwipe = dx < -minSwipeDistance;

    if (isLeftSwipe && currentIndex < dashboardTabs.length - 1) {
      goToTab(dashboardTabs[currentIndex + 1].id);
    } else if (isRightSwipe && currentIndex > 0) {
      goToTab(dashboardTabs[currentIndex - 1].id);
    }
  };

  const goToTab = (tabId) => {
    const nextTabId = dashboardTabs.some((tab) => tab.id === tabId)
      ? tabId
      : dashboardTabs[0]?.id || "insights";
    setActiveTab(nextTabId);
    // Keep all existing query params (accessToken, etc.) and only update `tab`.
    navigate(PERSON_DASHBOARD + handleParams("tab", nextTabId));
  };

  const setDashboardQueryParams = (paramsToSet) => {
    const urlParams = new URLSearchParams(window.location.search);
    Object.entries(paramsToSet || {}).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        urlParams.delete(key);
      } else {
        urlParams.set(key, String(value));
      }
    });
    navigate(PERSON_DASHBOARD + "?" + urlParams.toString());
  };

  useEffect(() => {
    const allowedIds = new Set(dashboardTabs.map((tab) => tab.id));
    const fallbackTabId = dashboardTabs[0]?.id || "insights";

    if (!tabFromUrl) {
      setActiveTab((current) =>
        allowedIds.has(current) ? current : fallbackTabId,
      );
      return;
    }

    const normalized = normalizeTabId(tabFromUrl);
    if (allowedIds.has(normalized)) {
      setActiveTab(normalized);
      return;
    }

    if (DASHBOARD_TABS.some((tab) => tab.id === normalized)) {
      setActiveTab(fallbackTabId);
      navigate(PERSON_DASHBOARD + handleParams("tab", fallbackTabId));
    }
  }, [dashboardTabs, navigate, tabFromUrl]);

  // Load the aggregated report so the "View Recent Report" card (Metrics) and
  // "See Mobility History" (Action) can show the last-tested date.
  useEffect(() => {
    const needsReport =
      activeTab === "metrics" || (activeTab === "action" && isPharmacyTenant);
    if (!needsReport || !activeSeniorId) return;
    let cancelled = false;
    setHasSeniorReport(true);
    setSeniorReport(null);
    // v95 engine reads the raw aggregated report; v2 reads the render document.
    const request =
      MOBILITY_REPORT_ENGINE === "v2"
        ? // Loaded on demand so the v95 build never pulls in the v2 client.
          import("../../../services/mobilityReportApi")
            .then(({ fetchMobilityReport }) =>
              fetchMobilityReport(activeSeniorId, { locale: reportLocale }),
            )
            .then((res) => res.document)
        : getSeniorReport(activeSeniorId).then(
            (res) => res?.data || res || null,
          );
    request
      .then((report) => {
        if (cancelled) return;
        setSeniorReport(report);
        setHasSeniorReport(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setSeniorReport(null);
        // 404 means the senior has no report yet — hide the card. Keep it for
        // transient/network errors so a retry can still open the report view.
        if (err?.response?.status === 404) setHasSeniorReport(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    activeTab,
    activeSeniorId,
    isPharmacyTenant,
    reportLocale,
    prodUs.reportVersion,
  ]);

  // Keep the card in sync with the latest completed screening. A missing
  // report is a normal first-use state and does not block starting a test.
  useEffect(() => {
    if (activeTab !== "insights" || !mobilityInsightSeniorId) return;

    let cancelled = false;
    setLatestMobilityInsight(null);
    getLatestMobilityInsight(mobilityInsightSeniorId)
      .then((response) => {
        if (cancelled) return;
        const payload = response?.data ?? response;
        const latest = Array.isArray(payload) ? payload[0] : payload;
        const result = latest?.result ?? latest?.latestResult;
        if (!latest || !result) return;

        setLatestMobilityInsight({
          reportId:
            latest.tempSeniorId ||
            latest.senior_id ||
            latest.seniorlife_id ||
            mobilityInsightSeniorId,
          timestamp:
            latest.timestamp || latest.assessedAt || latest.createdAt || null,
          riskLevel:
            result.risk_level ||
            result.riskLevel ||
            latest.risk_level ||
            latest.riskLevel ||
            null,
        });
      })
      .catch(() => {
        if (!cancelled) setLatestMobilityInsight(null);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, mobilityInsightSeniorId]);

  // Function to load test modules
  const loadTestModules = useCallback(
    (userInfo) => {
      modulePersonRef.current = userInfo?.id || null;
      if (!userInfo?.id || !userInfo?.seniorId) {
        dispatch(setAPILoader(false));
        return;
      }

      getTestModule(userInfo.id, userInfo.seniorId)
        .then((res) => {
          if (
            modulePersonRef.current !== userInfo.id ||
            String(userInfoFetch()?.id) !== String(userInfo.id)
          )
            return;
          const filteredModules = res?.data?.data || [];
          setRawModuleData(filteredModules);
          setModulesLoadedPerson(userInfo.id);
          sessionStorage.setItem(
            "testMModule",
            JSON.stringify(filteredModules),
          );
          sessionStorage.setItem(
            "measurementModulesPersonId",
            String(userInfo.id),
          );
          dispatch(setAPILoader(false));
        })
        .catch(() => {
          dispatch(setAPILoader(false));
        });
    },
    [dispatch],
  );

  // Initialize data
  useEffect(() => {
    let cancelled = false;
    modulePersonRef.current = null;
    setModulesLoadedPerson(null);
    dispatch(setAPILoader(true));

    let currentUserInfo = userInfoFetch() || currentUser;

    const exitToHost = () => {
      dispatch(setAPILoader(false));
      navigate(buildUserListPathFromCurrentQuery(), { replace: true });
    };

    const currentSeniorId = getSeniorIdFromUser(currentUserInfo);
    const seniorIdMismatch =
      selectedSeniorId && String(currentSeniorId) !== String(selectedSeniorId);

    if (seniorIdMismatch || (!currentSeniorId && selectedSeniorId)) {
      setIsResolvingSenior(true);
      getUserList(1, 999)
        .then((res) => {
          if (cancelled) return;
          const users = res?.data?.data || [];
          sessionStorage.setItem("userList", JSON.stringify(users));

          const foundIndex = users.findIndex(
            (user) =>
              String(getSeniorIdFromUser(user)) === String(selectedSeniorId),
          );

          if (foundIndex !== -1) {
            sessionStorage.setItem("userIndex", foundIndex);
            currentUserInfo = users[foundIndex];
            setCurrentUser(currentUserInfo);
            loadTestModules(currentUserInfo);
          } else {
            exitToHost();
          }
        })
        .catch(() => {
          if (!cancelled) exitToHost();
        })
        .finally(() => {
          if (!cancelled) setIsResolvingSenior(false);
        });
    } else if (currentSeniorId) {
      setIsResolvingSenior(false);
      setCurrentUser(currentUserInfo);
      loadTestModules(currentUserInfo);
    } else {
      setIsResolvingSenior(false);
      exitToHost();
    }

    // Simulate badge indicators (replace with real API data)
    setBadges({ insights: true, action: false });
    return () => {
      cancelled = true;
      modulePersonRef.current = null;
    };
  }, [dispatch, selectedSeniorId, navigate, loadTestModules]);

  // Prod_US: a new report means both test cards have a new last-tested date.
  useEffect(() => {
    if (!prodUsReportVersion) return;
    loadTestModules(userInfoFetch());
  }, [prodUsReportVersion, loadTestModules]);

  useEffect(() => {
    if (
      !loggedUserId ||
      String(staffDetails?.id || "") === String(loggedUserId)
    ) {
      return;
    }

    getLoggedUserInfo(loggedUserId)
      .then((res) => {
        const details = res?.data?.data || null;
        if (!details) return;
        setStaffDetails(details);
        sessionStorage.setItem("staffDetails", JSON.stringify(details));
      })
      .catch((error) => {
        console.error("DAP: load logged user tenant failed", error);
      });
  }, [loggedUserId, staffDetails?.id]);

  useEffect(() => {
    if (!prodUs.event) return;
    setSnackbarMessage(
      pt(prodUs.event === "ready" ? "report_ready" : "check_failed"),
    );
    setSnackbarSeverity(prodUs.event === "ready" ? "success" : "error");
    setSnackbarAnchor({ vertical: "bottom", horizontal: "center" });
    setSnackbarOpen(true);
    prodUs.clearEvent();
  }, [prodUs.event]);

  const handleBackClick = () => {
    navigate(buildUserListPathFromCurrentQuery(), { replace: true });
  };

  useEffect(() => {
    // Initialize once per person. A refresh must never undo staff deselection.
    if (selectionPersonRef.current !== activeSeniorId) {
      setSelectedMeasurementIds([]);
      selectionPersonRef.current = null;
    }
    if (
      !activeSeniorId ||
      modulesLoadedPerson !== userInfo?.id ||
      !resolveCustomerType(staffDetails || userInfo)
    )
      return;
    if (selectionPersonRef.current === null) {
      if (isProdUs) {
        setSelectedMeasurementIds(
          moduleData
            .filter(
              (module) =>
                PROD_US_MODULE_IDS.includes(Number(module.moduleId)) &&
                module.isOperationAllowed,
            )
            .map((module) => Number(module.moduleId)),
        );
        selectionPersonRef.current = activeSeniorId;
        return;
      }
      const sts = moduleData.find(
        (module) => module.moduleId === STS_MODULE_ID,
      );
      setSelectedMeasurementIds(sts?.isOperationAllowed ? [STS_MODULE_ID] : []);
      selectionPersonRef.current = activeSeniorId;
      return;
    }
    setSelectedMeasurementIds((ids) =>
      ids.filter((id) =>
        moduleData.some(
          (module) => module.moduleId === id && module.isOperationAllowed,
        ),
      ),
    );
  }, [
    moduleData,
    activeSeniorId,
    staffDetails,
    userInfo,
    modulesLoadedPerson,
    isProdUs,
  ]);

  const toggleMeasurementSelection = (module) => {
    if (!module?.isOperationAllowed || isProdUs) return; // Prod_US: both tests, always

    setSelectedMeasurementIds((ids) => {
      const moduleId = Number(module.moduleId);
      if (ids.includes(moduleId)) {
        return ids.filter((id) => id !== moduleId);
      }
      if (ids.length >= MAX_MEASUREMENT_SESSION_TESTS) {
        setSnackbarMessage(
          tDashboard("measurement_selection_help", {
            max: MAX_MEASUREMENT_SESSION_TESTS,
          }),
        );
        setSnackbarSeverity("info");
        setSnackbarAnchor({ vertical: "bottom", horizontal: "center" });
        setSnackbarOpen(true);
        return ids;
      }
      return [...ids, moduleId];
    });
  };

  const handleStartMeasurementSession = async () => {
    if (!activeSeniorId || isCheckingHeight) return;
    if (isProdUs && (prodUsBusy || !prodUsCanStart)) {
      if (!prodUsBusy) setProdUsCreditsOpen(true);
      return;
    }

    const personAtStart = String(activeSeniorId);
    const stillCurrentPerson = () => {
      const latest = measurementContextRef.current;
      const params = getParams();
      return (
        String(latest?.personId) === personAtStart &&
        String(latest?.userId) === personAtStart &&
        String(modulePersonRef.current) === personAtStart &&
        String(userInfoFetch()?.id) === personAtStart &&
        (!params.seniorId || String(params.seniorId) === personAtStart) &&
        (!params.seniorUuid || String(params.seniorUuid) === personAtStart)
      );
    };

    setIsCheckingHeight(true);
    try {
      const profile = await getPatientProfile(activeSeniorId);
      if (!stillCurrentPerson()) return;
      if (!hasValidProfileHeight(profile)) {
        setShowHeightPrompt(true);
        return;
      }
    } catch (error) {
      if (!stillCurrentPerson()) return;
      setSnackbarMessage(
        error?.response?.data?.message ||
          error?.message ||
          tSts("height_check_failed"),
      );
      setSnackbarSeverity("error");
      setSnackbarAnchor({ vertical: "bottom", horizontal: "center" });
      setSnackbarOpen(true);
      return;
    } finally {
      setIsCheckingHeight(false);
    }

    if (!stillCurrentPerson()) return;
    const latest = measurementContextRef.current;
    const selectedModules = latest.modules.filter(
      (module) =>
        latest.selectedIds.includes(Number(module.moduleId)) &&
        module.isOperationAllowed &&
        (Number(module.moduleId) !== STS_MODULE_ID || isStsEligible()),
    );
    const session = startMeasurementSession(selectedModules, activeSeniorId);
    const firstModule = session?.queue?.[0];
    const route = getSessionModuleRoute(firstModule?.moduleId);

    if (!firstModule || !route) return;

    prepareModuleForUpload(firstModule);
    navigate(route + `${getSearchParams()}`);
  };

  const handleAddHeight = () => {
    const params = new URLSearchParams(window.location.search);
    params.set("seniorUuid", activeSeniorId);
    params.set("focus", "height");
    setShowHeightPrompt(false);
    navigate(`${EDIT_TEST_PERSON}?${params.toString()}`);
  };

  const handleViewReport = (url, moduleId, e) => {
    e?.stopPropagation();
    const params = new URLSearchParams(window.location.search);
    if (url) params.set("reportUrl", url);
    if (moduleId != null) params.set("moduleId", String(moduleId));
    if (activeSeniorId) {
      params.set("seniorId", activeSeniorId);
      params.set("seniorUuid", activeSeniorId);
    }
    const query = params.toString();
    navigate(query ? `${MOBILITY_REPORT}?${query}` : MOBILITY_REPORT);
  };

  // Aggregated recent report for the whole senior — opens the same report view
  // as the per-module report, but the view fetches GET /v6/seniors/:id/report.
  const handleViewSeniorReport = () => {
    const params = new URLSearchParams(window.location.search);
    params.set("reportSource", "v6");
    params.delete("reportUrl");
    params.delete("moduleId");
    // A dashboard can carry a risk assessment or older report reference. Opening
    // the current mobility report must not reuse that unrelated identity.
    params.delete("reportId");
    params.delete("report_id");
    params.delete("revision");
    params.delete("activity_id");
    params.delete("activityId");
    if (activeSeniorId) {
      params.set("seniorId", activeSeniorId);
      params.set("seniorUuid", activeSeniorId);
    }
    const query = params.toString();
    navigate(query ? `${MOBILITY_REPORT}?${query}` : MOBILITY_REPORT);
  };

  const handleActionPlanAction = (action) => {
    switch (action?.destination || action?.id) {
      case "metrics":
        goToTab("metrics");
        break;
      case "insights":
        goToTab("insights");
        break;
      case "edit_patient": {
        const params = new URLSearchParams(window.location.search);
        if (activeSeniorId) params.set("seniorUuid", activeSeniorId);
        navigate(`${EDIT_TEST_PERSON}?${params.toString()}`);
        break;
      }
      case "report": {
        if (MOBILITY_REPORT_ENGINE === "v2") {
          handleViewSeniorReport();
          break;
        }
        // Open the most recent report, not just the first module that happens
        // to carry a URL — `find` returned whatever came first in array order.
        const latestReport = moduleData
          .filter(
            (module) =>
              module.moduleId !== STS_MODULE_ID &&
              module.reportURL &&
              module.isReportAvailable,
          )
          .reduce(
            (latest, module) =>
              !latest ||
              (module.lastTestTaken || 0) > (latest.lastTestTaken || 0)
                ? module
                : latest,
            null,
          );
        handleViewReport(latestReport?.reportURL, latestReport?.moduleId);
        break;
      }
      default:
        break;
    }
  };

  const handleViewAssessmentReport = async (assessment) => {
    if (assessment?.id) {
      suppressReportDeepLinkRef.current = true;
      setDashboardQueryParams({
        tab: "insights",
        reportId: assessment.id,
      });
    }
    dispatch(setAPILoader(true));
    try {
      const response = await getRiskAssessmentById(assessment.id, userInfo.id);
      const detailData = response.data || response;
      setSelectedAssessment({ ...assessment, ...detailData });
    } catch (err) {
      console.error("Error fetching assessment details:", err);
      // Fallback
      setSelectedAssessment(assessment);
    } finally {
      dispatch(setAPILoader(false));
      setShowAssessmentList(false);
      setShowMobilityInsights(true);
    }
  };

  const handleAssessmentListClose = () => {
    setShowAssessmentList(false);
    setAssessments([]);
  };

  const handleOpenMobilityInsightsByReportId = async (
    reportId,
    syncUrl = true,
  ) => {
    if (!reportId) return;

    if (syncUrl) {
      suppressReportDeepLinkRef.current = true;
      setDashboardQueryParams({
        tab: "insights",
        reportId,
      });
    }
    dispatch(setAPILoader(true));
    try {
      const seniorUuid = userInfo?.id || selectedSeniorId;
      const response = await getRiskAssessmentById(reportId, seniorUuid);
      const detailData = response?.data || response;
      setSelectedAssessment(detailData);
    } catch (err) {
      console.error("Error fetching risk assessment by report id:", err);
      setSelectedAssessment({ id: reportId });
    } finally {
      dispatch(setAPILoader(false));
      setShowAssessmentList(false);
      setShowQuestionnaire(false);
      setShowMobilityInsights(true);
    }
  };

  // Deep link support: /person-dashboard?tab=risk&reportId=<id>
  useEffect(() => {
    if (!reportIdFromUrl) return;
    if (suppressReportDeepLinkRef.current) {
      suppressReportDeepLinkRef.current = false;
      return;
    }
    if (showMobilityInsights && selectedAssessment?.id === reportIdFromUrl) {
      return;
    }
    setActiveTab("insights");
    handleOpenMobilityInsightsByReportId(reportIdFromUrl, false);
  }, [reportIdFromUrl]);

  const handleMobilityInsightsClose = () => {
    setDashboardQueryParams({
      tab: "insights",
      reportId: null,
    });
    setShowMobilityInsights(false);
    setSelectedAssessment(null);
    // If we have assessments loaded, go back to list view
    if (assessments.length > 0) {
      setShowAssessmentList(true);
    }
    // Update badge to show assessment completed
    setBadges((prev) => ({ ...prev, insights: false }));
  };

  const handleQuestionnaireComplete = (submitData) => {
    console.log("Questionnaire completed:", submitData);
    setShowQuestionnaire(false);
    // Update badge to show assessment completed
    setBadges((prev) => ({ ...prev, insights: false }));
    // TODO: Show success message or navigate to results
  };

  const handleQuestionnaireClose = () => {
    setShowQuestionnaire(false);
  };

  const handleCheckVitals = async () => {
    if (!isWebView()) {
      if (FEATURE_FLAGS.ENABLE_VITALS_DEMO) {
        navigate(VITALS + getSearchParams());
      } else {
        setSnackbarMessage(
          "Vitals measurement is available in the Inochi mobile app.",
        );
        setSnackbarSeverity("info");
        setSnackbarOpen(true);
      }
      return;
    }

    if (isVitalsCaptureActive) {
      setSnackbarMessage("A vitals measurement is already in progress.");
      setSnackbarSeverity("info");
      setSnackbarOpen(true);
      return;
    }

    if (!nativeVitalsCapability?.compatible) {
      setSnackbarMessage(
        "Update Inochi to a build with Welltra VitalsSDK 5.9.3 to measure vitals.",
      );
      setSnackbarSeverity("info");
      setSnackbarOpen(true);
      return;
    }

    if (!activeSeniorId || isPreparingVitals) return;
    setIsPreparingVitals(true);

    try {
      let initialVitals = null;
      try {
        initialVitals = getLatestStoredVitals(await listVitals(activeSeniorId));
      } catch (error) {
        // A history lookup failure should not prevent a new measurement.
        console.warn("[vitals] unable to load saved readings", error);
      }

      if (!initialVitals) {
        setShowVitalsInstructions(true);
        return;
      }

      const profile = await getPatientProfile(activeSeniorId);
      const { subjectInfo, missingFields, valid } = buildVitalsSubjectInfo(
        profile,
        userInfo,
      );
      if (!valid) {
        setSnackbarMessage(
          `Complete the profile before measuring vitals: ${missingFields.join(", ")}.`,
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      const result = startNativeVitalsCapture({
        subjectInfo,
        locale: getCurrentLocale(),
        initialVitals,
      });
      if (!result.started) {
        setSnackbarMessage(
          "Unable to open the saved vitals. Please try again.",
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to open the saved vitals.",
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsPreparingVitals(false);
    }
  };

  const handleStartVitalsCapture = async () => {
    if (!activeSeniorId || isPreparingVitals) return;
    setIsPreparingVitals(true);

    try {
      const profile = await getPatientProfile(activeSeniorId);
      const { subjectInfo, missingFields, valid } = buildVitalsSubjectInfo(
        profile,
        userInfo,
      );
      if (!valid) {
        setSnackbarMessage(
          `Complete the profile before measuring vitals: ${missingFields.join(", ")}.`,
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      setShowVitalsInstructions(false);
      await new Promise((resolve) => setTimeout(resolve, 50));
      const result = startNativeVitalsCapture({
        subjectInfo,
        locale: getCurrentLocale(),
      });
      if (!result.started) {
        setSnackbarMessage(
          "Unable to start the vitals measurement. Please try again.",
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }
    } catch (error) {
      setSnackbarMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to verify the profile for vitals measurement.",
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsPreparingVitals(false);
    }
  };

  const handleViewAlertsActivities = () => {
    navigate(ACTIVITY_MONITORING + getSearchParams());
  };

  // Tab icon components
  const TabIcon = ({ type, isActive }) => {
    const color = isActive ? "var(--primary-color)" : "black";

    const icons = {
      insights: (
        <svg width="24" height="24" viewBox="0 0 20 20" fill="none">
          <path
            d="M10 2.5L3 6.5V11C3 14.5 6 17.5 10 18.5C14 17.5 17 14.5 17 11V6.5L10 2.5Z"
            fill="none"
            stroke={color}
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <path
            d="M10 7V10.5"
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <circle cx="10" cy="13" r="1" fill={color} />
        </svg>
      ),
      metrics: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="12" width="4" height="8" rx="1" fill={color} />
          <rect x="10" y="7" width="4" height="13" rx="1" fill={color} />
          <rect x="17" y="4" width="4" height="16" rx="1" fill={color} />
        </svg>
      ),
      monitor: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 21C12 21 3.5 15 3.5 9C3.5 6.5 5.5 4.5 8 4.5C9.5 4.5 10.9 5.2 12 6.3C13.1 5.2 14.5 4.5 16 4.5C18.5 4.5 20.5 6.5 20.5 9C20.5 15 12 21 12 21Z"
            fill={color}
            stroke={color}
            strokeWidth="1.2"
            strokeLinejoin="round"
          />
        </svg>
      ),
      action: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect
            x="5"
            y="3"
            width="14"
            height="18"
            rx="2"
            stroke={color}
            strokeWidth="1.6"
          />
          <path
            d="M9 3v2h6V3"
            stroke={color}
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8 11h8M8 15h5"
            stroke={color}
            strokeWidth="1.4"
            strokeLinecap="round"
          />
        </svg>
      ),
    };

    return icons[type] || null;
  };

  // Render Risk Tab Content
  const renderRiskContent = () => (
    <div className="tab-content-inner">
      <section className="dashboard-section">
        <Typography className="section-title">{get_risk_insights}</Typography>
        <div className="insights-card-list">
          {INSIGHT_DESTINATIONS.map((insight) => (
            <Paper
              key={insight.id}
              elevation={0}
              className="dashboard-card feature-card insight-link-card"
              role="button"
              tabIndex={0}
              onClick={() => handleOpenInsight(insight)}
              onKeyDown={(event) =>
                handleCardKeyDown(event, () => handleOpenInsight(insight))
              }
            >
              <div className="card-header">
                <div className="card-icon mobility-icon">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.5 5.5C14.6 5.5 15.5 4.6 15.5 3.5C15.5 2.4 14.6 1.5 13.5 1.5C12.4 1.5 11.5 2.4 11.5 3.5C11.5 4.6 12.4 5.5 13.5 5.5ZM9.8 8.9L7 23H9.1L10.9 15L13 17V23H15V15.5L12.9 13.5L13.5 10.5C14.8 12 16.8 13 19 13V11C17.1 11 15.5 10 14.7 8.6L13.7 7C13.3 6.4 12.7 6 12 6C11.7 6 11.5 6.1 11.2 6.1L6 8.3V13H8V9.6L9.8 8.9Z"
                      fill="var(--primary-color)"
                    />
                  </svg>
                </div>
                <div className="card-title-content">
                  <Typography className="card-title">
                    {personDashboardStrings[insight.titleKey]}
                  </Typography>
                  <Typography className="card-subtitle">
                    {insight.id === "mobility"
                      ? mobility_insights_desc
                      : tDashboard(insight.descriptionKey, {
                          person: personLabels.singularLower,
                        })}
                  </Typography>
                </div>
                <div className="card-arrow">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.59 16.59L13.17 12L8.59 7.41L10 6L16 12L10 18L8.59 16.59Z"
                      fill="#9E9E9E"
                    />
                  </svg>
                </div>
              </div>
              {insight.id === "mobility" && latestMobilityInsight && (
                <div
                  className="insight-result-summary"
                  role="link"
                  tabIndex={0}
                  aria-label={`${personDashboardStrings[insight.titleKey]} ${personDashboardStrings.view_recent_result}`}
                  onClick={(event) =>
                    handleOpenLatestMobilityReport(insight, event)
                  }
                  onKeyDown={(event) =>
                    handleCardKeyDown(event, () =>
                      handleOpenLatestMobilityReport(insight, event),
                    )
                  }
                >
                  <div>
                    <Typography className="insight-result-value">
                      <span
                        className={`insight-risk-status ${mobilityRiskTone(
                          latestMobilityInsight.riskLevel,
                        )}`}
                      >
                        {latestMobilityInsight.riskLevel
                          ? `${String(latestMobilityInsight.riskLevel)
                              .charAt(0)
                              .toUpperCase()}${String(
                              latestMobilityInsight.riskLevel,
                            ).slice(1)} risk`
                          : not_available}
                      </span>
                    </Typography>
                    {latestMobilityInsight.timestamp && (
                      <Typography className="insight-result-date">
                        {last_tested}{" "}
                        {dateFormatHanlder(
                          latestMobilityInsight.timestamp,
                          getCurrentLocale(),
                        )}
                      </Typography>
                    )}
                  </div>
                  <span className="insight-result-view" aria-hidden="true">
                    {historyStrings.view_report}
                  </span>
                </div>
              )}
            </Paper>
          ))}
        </div>
      </section>
    </div>
  );

  // Render Metrics Tab Content
  const renderMetricsContent = () => (
    <div className="tab-content-inner metrics-session-picker">
      <section className="dashboard-section">
        <div className="metrics-top-actions">
          {prodUsBusy ? (
            <Paper
              elevation={0}
              className="recent-report-select-card pu-analyzing"
              role="status"
            >
              <div className="recent-report-icon">
                <svg width="39" height="39" viewBox="0 0 48 48" fill="none">
                  <rect width="48" height="48" rx="12" fill="#1155CC" />
                  <path
                    d="M12 12V36H36"
                    stroke="#FFFFFF"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M33 18L25 26L20 21L16 25"
                    stroke="#FFFFFF"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="recent-report-body">
                <Typography className="recent-report-title">
                  {pt("analyzing")}
                </Typography>
                <Typography className="recent-report-meta">
                  {pt("analyzing_sub")}
                </Typography>
              </div>
            </Paper>
          ) : null}
          {!prodUsBusy && hasSeniorReport && (
            <Paper
              elevation={0}
              className="recent-report-select-card"
              role="button"
              tabIndex={0}
              onClick={handleViewSeniorReport}
              onKeyDown={(event) =>
                handleCardKeyDown(event, handleViewSeniorReport)
              }
            >
              <div className="recent-report-icon">
                <svg
                  width="39"
                  height="39"
                  viewBox="0 0 48 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="48" height="48" rx="12" fill="#1155CC" />
                  <path
                    d="M12 12V36H36"
                    stroke="#FFFFFF"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M33 18L25 26L20 21L16 25"
                    stroke="#FFFFFF"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="recent-report-body">
                <Typography className="recent-report-title">
                  {view_recent_report}
                </Typography>
                <Typography className="recent-report-meta">
                  {MOBILITY_REPORT_ENGINE === "v2"
                    ? seniorReport?.partner_summary?.assessment_date_label ||
                      seniorReport?.assessment?.date_label ||
                      seniorReport?.assessment?.last_assessment_date_label ||
                      view_recent_report_desc.replace(
                        "test person",
                        personLabels.singularLower,
                      )
                    : seniorReport?.timeStamp
                      ? `${last_tested} ${dateFormatHanlder(
                          seniorReport.timeStamp,
                          getCurrentLocale(),
                        )}`
                      : view_recent_report_desc.replace(
                          "test person",
                          personLabels.singularLower,
                        )}
                </Typography>
              </div>
              <div className="report-chevron">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 6L15 12L9 18"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </Paper>
          )}
        </div>

        {hasSeniorReport || prodUsBusy ? (
          <div className="picker-divider" />
        ) : null}

        {isProdUs ? (
          <div className="measurement-picker-heading pu-heading">
            <Typography className="picker-title">
              {exercise_measurement_modules}
            </Typography>
            {prodUs.loaded ? (
              <span
                className={`pu-pill${prodUs.credits > 0 ? "" : " zero"}`}
                role="status"
              >
                {pt("credits", { n: prodUs.credits })}
              </span>
            ) : null}
          </div>
        ) : (
          <div className="measurement-picker-heading">
            <Typography className="picker-title">
              {exercise_measurement_modules}
              <span className="picker-count">
                {selectedMeasurementIds.length}/{MAX_MEASUREMENT_SESSION_TESTS}
              </span>
            </Typography>
            <Typography className="picker-subtitle">
              {tDashboard("measurement_selection_help", {
                max: MAX_MEASUREMENT_SESSION_TESTS,
              })}
            </Typography>
          </div>
        )}

        <div className="measurement-card-list">
          {moduleData.map((module) => {
            const moduleId = Number(module.moduleId);
            const isSelected =
              selectedMeasurementIds.includes(moduleId) &&
              (!isProdUs || (prodUsCanStart && !prodUsBusy));
            const atLimit =
              selectedMeasurementIds.length >= MAX_MEASUREMENT_SESSION_TESTS;
            const isDisabled =
              !module.isOperationAllowed || (!isSelected && atLimit);

            return (
              <Paper
                key={module.moduleId}
                elevation={0}
                className={`measurement-select-card ${isSelected ? "selected" : ""} ${isDisabled ? "disabled" : ""}`}
                role="checkbox"
                aria-checked={isSelected}
                aria-disabled={isDisabled}
                tabIndex={module.isOperationAllowed ? 0 : -1}
                onClick={() => toggleMeasurementSelection(module)}
                onKeyDown={(event) =>
                  handleCardKeyDown(event, () =>
                    toggleMeasurementSelection(module),
                  )
                }
              >
                <div className="measurement-card-icon">
                  {moduleId === STS_MODULE_ID ? (
                    <StsIllustration icon />
                  ) : (
                    <svg
                      width="39"
                      height="39"
                      viewBox="0 0 30 30"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M20.3 24.1c.3.6 0 1.2-.5 1.5-.5.3-1.2.1-1.5-.4l-2.1-4.2-3.1-3.1c-.3-.3-.4-.7-.3-1.1l.8-4.7-2.3.6-1.9 2.9c-.3.5-1 .6-1.5.3-.5-.3-.7-1-.4-1.5l2.2-3.3c.2-.3.4-.4.7-.5l4.2-1.1c.4-.1.8 0 1.1.3l3.1 3 3 1c.6.2.9.8.7 1.4-.2.5-.8.9-1.3.7l-3.3-1.1c-.2-.1-.3-.2-.4-.3l-1.7-1.7-.6 3.9 3 3 2.2 4.5Z"
                        fill="#2E4D72"
                      />
                      <path
                        d="M10.5 25.2c-.4.5-1 .6-1.5.2-.4-.3-.6-1-.3-1.5l3.2-4.3c.4-.5 1-.6 1.5-.3.5.4.6 1 .2 1.5l-3.2 4.4Z"
                        fill="#2E4D72"
                      />
                      <path
                        d="M15.7 4.1c1.2-.2 2.3.6 2.5 1.8.2 1.2-.6 2.3-1.7 2.5-1.2.2-2.3-.5-2.5-1.7-.2-1.2.5-2.3 1.7-2.5Z"
                        fill="#2E4D72"
                      />
                    </svg>
                  )}
                  <span>{getModuleBadge(module)}</span>
                </div>
                <div className="measurement-card-body">
                  <Typography className="measurement-card-title">
                    {getModuleName(module)}
                  </Typography>
                  <Typography className="measurement-card-desc">
                    {getModuleDescription(module)}
                  </Typography>
                  <Typography
                    className={`measurement-card-date ${module.lastTestTaken ? "" : "never"}`}
                  >
                    {module.lastTestTaken
                      ? `${last_tested} ${dateFormatHanlder(
                          module.lastTestTaken,
                          getCurrentLocale(),
                        )}`
                      : never_tested}
                  </Typography>
                </div>
                <div className="selection-circle" aria-hidden="true">
                  {isSelected && (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5 12L10 17L20 6"
                        stroke="#FFFFFF"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              </Paper>
            );
          })}
        </div>

        {isProdUs ? (
          <div className="measurement-picker-footer">
            {prodUsBusy || !prodUs.loaded ? null : prodUsCanStart ? (
              <>
                <button
                  className="btn btn-primary"
                  disabled={
                    selectedMeasurementIds.length < PROD_US_MODULE_IDS.length ||
                    isCheckingHeight
                  }
                  onClick={handleStartMeasurementSession}
                >
                  {isCheckingHeight
                    ? checking_profile
                    : tDashboard("start_tests", {
                        count: PROD_US_MODULE_IDS.length,
                      })}
                </button>
                <button
                  type="button"
                  className="pu-credits-link"
                  onClick={() => setProdUsCreditsOpen(true)}
                >
                  {pt("request_credits")}
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn btn-primary"
                  onClick={() => setProdUsCreditsOpen(true)}
                >
                  {pt("request_credits")}
                </button>
                <p className="pu-note">{pt("none_left")}</p>
              </>
            )}
            <ProdUsCreditsModal
              open={prodUsCreditsOpen}
              onClose={() => setProdUsCreditsOpen(false)}
            />
          </div>
        ) : (
          <div className="measurement-picker-footer">
            <button
              className="btn btn-primary"
              disabled={selectedMeasurementIds.length === 0 || isCheckingHeight}
              onClick={handleStartMeasurementSession}
            >
              {isCheckingHeight
                ? checking_profile
                : selectedMeasurementIds.length
                  ? tDashboard(
                      selectedMeasurementIds.length > 1
                        ? "start_tests"
                        : "start_test",
                      { count: selectedMeasurementIds.length },
                    )
                  : start_session}
            </button>
          </div>
        )}
      </section>
    </div>
  );

  // Render Monitor Tab Content
  const renderMonitorContent = () => (
    <div className="tab-content-inner">
      {/* Vital Signs Section */}
      <section className="dashboard-section">
        <Typography className="section-title">{check_vitals}</Typography>
        <Paper elevation={0} className="dashboard-card feature-card">
          <Typography className="card-description">
            {check_vitals_desc}
          </Typography>
          <div className="card-actions">
            <button
              className="btn btn-primary"
              onClick={handleCheckVitals}
              disabled={isPreparingVitals}
            >
              {isPreparingVitals ? checking_profile : check_vitals_btn}
            </button>
          </div>
        </Paper>
      </section>

      {/* Activity Analysis Section */}
      <section className="dashboard-section">
        <Typography className="section-title">{activity_analysis}</Typography>
        <Paper elevation={0} className="dashboard-card feature-card">
          <Typography className="card-description">
            {activity_analysis_desc}
          </Typography>
          <div className="card-actions">
            <button
              className="btn btn-primary"
              onClick={handleViewAlertsActivities}
            >
              {view_alerts_activities}
            </button>
          </div>
        </Paper>
      </section>
    </div>
  );

  // Dynamic Action Plan — rendered in the Action tab and in the Metrics
  // overview (with `onSaved` so Previous Actions updates straight away).
  const renderDynamicActionPlan = (extraProps = {}) => {
    const actionPlanPatientName = resolveActionPlanPatientName(userInfo);
    const actionPlanTenant = resolveActionPlanTenant(staffDetails || userInfo);
    const actionPlanUserType = resolveActionPlanUserType(
      staffDetails || userInfo,
    );
    const params = new URLSearchParams(window.location.search);
    const previewData = isLocalDapPreview(params)
      ? getMockActionPlan(actionPlanTenant, actionPlanUserType)
      : null;

    if (isResolvingSenior && activeSeniorId && !actionPlanPatientName) {
      return (
        <div style={{ padding: 24, textAlign: "center", color: "#64748b" }}>
          {tDashboard("loading")}
        </div>
      );
    }

    return (
      <Suspense
        fallback={
          <div style={{ padding: 24, textAlign: "center", color: "#64748b" }}>
            {tDashboard("loading")}
          </div>
        }
      >
        <DynamicActionPlan
          key={`${actionPlanPatientName}-${actionPlanTenant}-${actionPlanUserType}`}
          data={previewData}
          patientName={actionPlanPatientName}
          seniorId={previewData ? "" : activeSeniorId}
          tenantType={actionPlanTenant}
          userType={actionPlanUserType}
          currentUserEmail={staffDetails?.email || userInfo?.email || ""}
          hidePatientHeader
          onNavigateTab={goToTab}
          onActivityTrend={handleViewAlertsActivities}
          onCheckVitals={handleCheckVitals}
          onAction={handleActionPlanAction}
          onViewReport={handleViewSeniorReport}
          {...extraProps}
        />
      </Suspense>
    );
  };

  // Action tab = the DAP87 patient page (opened directly from the patient
  // list): plan → See Mobility History → Previous Actions, with a
  // "New assessment needed" card when there is no current Mobility Check.
  // Capturing metrics stays in the Metrics tab, unchanged.
  const renderActionContent = () => (
    <div className="tab-content-inner dap-tab-content">
      {!isPharmacyTenant ? (
        renderDynamicActionPlan()
      ) : (
        <MobilityOverview
          seniorId={activeSeniorId}
          tenantType={resolveActionPlanTenant(staffDetails || userInfo)}
          hasReport={hasSeniorReport}
          reportDocument={
            MOBILITY_REPORT_ENGINE === "v2" ? seniorReport : undefined
          }
          reportDate={
            MOBILITY_REPORT_ENGINE === "v2"
              ? undefined
              : seniorReport?.timeStamp
          }
          renderPlan={(extraProps) => renderDynamicActionPlan(extraProps)}
          onViewHistory={handleViewSeniorReport}
          onTakeAssessment={() => goToTab("metrics")}
        />
      )}
    </div>
  );

  // Render content based on active tab
  const renderTabContent = () => {
    if (!dashboardTabs.some((tab) => tab.id === activeTab)) {
      return renderRiskContent();
    }

    switch (activeTab) {
      case "insights":
        return renderRiskContent();
      case "metrics":
        return renderMetricsContent();
      case "monitor":
        return renderMonitorContent();
      case "action":
        return renderActionContent();
      default:
        return renderRiskContent();
    }
  };

  const renderInsightFrame = () => {
    if (!selectedInsightFrame) return null;

    return (
      <div className="insight-frame-view">
        <header className="insight-frame-header">
          <button
            type="button"
            className="back-btn"
            onClick={() => setSelectedInsightFrame(null)}
            aria-label={tDashboard("back_to_insights")}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M19 12H5m0 0l7-7m-7 7l7 7"
                stroke="#000000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <Typography className="insight-frame-title">
            {selectedInsightFrame.title}
          </Typography>
        </header>
        <div className="insight-frame-scroll">
          <iframe
            title={selectedInsightFrame.title}
            className="insight-frame"
            src={selectedInsightFrame.src}
            allow="camera; microphone; clipboard-read; clipboard-write"
            scrolling="yes"
          />
        </div>
      </div>
    );
  };

  // Render assessment list if active
  if (showAssessmentList) {
    return (
      <AssessmentList
        assessments={assessments}
        onClose={handleAssessmentListClose}
        onViewReport={handleViewAssessmentReport}
        onRetake={() => {
          setShowAssessmentList(false);
          setShowQuestionnaire(true);
        }}
      />
    );
  }

  // Render mobility insights (detail view) if active
  if (showMobilityInsights) {
    return (
      <MobilityInsights
        assessmentData={selectedAssessment}
        seniorId={activeSeniorId || "unknown"}
        onClose={handleMobilityInsightsClose}
        onRetake={() => {
          setShowMobilityInsights(false);
          setSelectedAssessment(null);
          setShowQuestionnaire(true);
        }}
      />
    );
  }

  // Render questionnaire if active
  if (showQuestionnaire) {
    return (
      <RiskQuestionnaire
        seniorId={activeSeniorId || "unknown"}
        onComplete={handleQuestionnaireComplete}
        onClose={handleQuestionnaireClose}
      />
    );
  }

  return (
    <div
      className={`person-dashboard-v2${isNavHidden ? " nav-hidden" : ""}${isWebView() ? " native-webview" : ""}`}
    >
      {/* Pinned Header */}
      <div className="pinned-header">
        <div className="header-left">
          {isProdUs ? (
            <button
              className="back-btn"
              onClick={() => setProdUsMenuOpen(true)}
              aria-label={pt("menu")}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 7h16M4 12h16M4 17h16"
                  stroke="#000000"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          ) : (
            <button className="back-btn" onClick={handleBackClick}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M19 12H5m0 0l7-7m-7 7l7 7"
                  stroke="#000000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
          <div
            className="user-info"
            onClick={() => {
              if (isProdUs) {
                setProdUsDetailsOpen(true);
              } else if (userInfo?.id) {
                navigate(
                  EDIT_TEST_PERSON + handleParams("seniorUuid", userInfo.id),
                );
              }
            }}
            style={{ cursor: "pointer" }}
          >
            <InitialsAvatar
              name={formatName(userInfo, getCurrentLocale())}
              className="user-avatar"
            />
            <div className="user-details">
              <Typography className="user-name">
                {formatName(userInfo, getCurrentLocale())}
              </Typography>
              <Typography className="user-meta">
                {genderLabel(userInfo?.gender)}
                {userInfo?.age > 0 && ` | ${userInfo?.age}`}
                {personExternalId ? (
                  <span className="user-meta-id">
                    <i> | </i>
                    {tDashboard("id_label")}{" "}
                    <span className="notranslate">{personExternalId}</span>
                  </span>
                ) : null}
              </Typography>
            </div>
          </div>
        </div>
        <div className="header-right">
          {isProdUs ? (
            <button
              type="button"
              className="pu-details-btn"
              onClick={() => setProdUsDetailsOpen(true)}
            >
              {pt("details")}
            </button>
          ) : (
            <MoreActionMenu userInfo={userInfo} seniorId={activeSeniorId} />
          )}
        </div>
      </div>

      {/* Swipeable Content Area */}
      <div className="tab-content" ref={contentRef}>
        {renderTabContent()}
      </div>

      {isProdUs ? (
        <ProdUsDetails
          open={prodUsDetailsOpen}
          onClose={() => setProdUsDetailsOpen(false)}
          onSaved={(person) => {
            setCurrentUser(person);
            setSnackbarMessage(pt("det_saved"));
            setSnackbarSeverity("success");
            setSnackbarAnchor({ vertical: "bottom", horizontal: "center" });
            setSnackbarOpen(true);
          }}
        />
      ) : null}

      {isProdUs ? (
        <ProdUsMenu
          open={prodUsMenuOpen}
          onClose={() => setProdUsMenuOpen(false)}
          person={userInfo}
          onViewReport={
            hasSeniorReport && !prodUsBusy ? handleViewSeniorReport : null
          }
        />
      ) : null}

      {/* Bottom Navigation — hidden when host passes ?hideNav=true and
          while an insight iframe is open (it renders as a full-screen view). */}
      {!isNavHidden && !selectedInsightFrame && (
        <nav
          className="bottom-nav"
          onTouchStart={onNavTouchStart}
          onTouchMove={onNavTouchMove}
          onTouchEnd={onNavTouchEnd}
        >
          {dashboardTabs.map((tab) => (
            <button
              key={tab.id}
              className={`nav-item ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => goToTab(tab.id)}
              disabled={tab.disabled}
            >
              <div className="nav-icon-wrapper">
                <TabIcon type={tab.icon} isActive={activeTab === tab.id} />
                {badges[tab.id] && <span className="badge-dot" />}
              </div>
              <span className="nav-label">{bottomNavLabels[tab.labelKey]}</span>
            </button>
          ))}
        </nav>
      )}

      {/* Notification Snackbar */}
      <Dialog
        open={showHeightPrompt}
        onClose={() => setShowHeightPrompt(false)}
        aria-labelledby="height-required-title"
      >
        <DialogTitle id="height-required-title">{height_required}</DialogTitle>
        <DialogContent>
          {tDashboard("height_required_description", {
            person: personLabels.singularLower,
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHeightPrompt(false)}>{cancel}</Button>
          <Button variant="contained" onClick={handleAddHeight} autoFocus>
            {add_height}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showVitalsInstructions}
        onClose={() => !isPreparingVitals && setShowVitalsInstructions(false)}
        aria-labelledby="vitals-instructions-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="vitals-instructions-title">
          {tDashboard("vitals_intro_title")}
        </DialogTitle>
        <DialogContent dividers>
          <Typography component="p" sx={{ mb: 1.5 }}>
            {tDashboard("vitals_intro_body")}
          </Typography>
          <Typography
            component="ul"
            sx={{ pl: 3, m: 0, display: "grid", gap: 1 }}
          >
            <li>{tDashboard("vitals_tip_posture")}</li>
            <li>{tDashboard("vitals_tip_distance")}</li>
            <li>{tDashboard("vitals_tip_lighting")}</li>
            <li>{tDashboard("vitals_tip_still")}</li>
            <li>{tDashboard("vitals_tip_retry")}</li>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowVitalsInstructions(false)}
            disabled={isPreparingVitals}
          >
            {tDashboard("cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleStartVitalsCapture}
            disabled={isPreparingVitals}
            autoFocus
          >
            {isPreparingVitals
              ? tDashboard("checking_profile")
              : tDashboard("vitals_start_test")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(lastCapturedVitals)}
        onClose={() => setLastCapturedVitals(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{tDashboard("vitals_result_title")}</DialogTitle>
        <DialogContent>
          <Typography component="div" sx={{ display: "grid", gap: 1.5, pt: 1 }}>
            <div>
              {tDashboard("vitals_result_hr", {
                value: lastCapturedVitals?.hr ?? "",
              })}
            </div>
            <div>
              {tDashboard("vitals_result_bp", {
                sbp: lastCapturedVitals?.sbp ?? "",
                dbp: lastCapturedVitals?.dbp ?? "",
              })}
            </div>
            <div>
              {tDashboard("vitals_result_rr", {
                value: lastCapturedVitals?.rr ?? "",
              })}
            </div>
            <div>
              {tDashboard("vitals_result_spo2", {
                value: lastCapturedVitals?.spo2 ?? "",
              })}
            </div>
            {Number.isFinite(lastCapturedVitals?.hrv_sdnn) ? (
              <div>
                {tDashboard("vitals_result_hrv", {
                  value: lastCapturedVitals.hrv_sdnn,
                })}
              </div>
            ) : null}
            {Number.isFinite(lastCapturedVitals?.stress) ? (
              <div>
                {tDashboard("vitals_result_stress", {
                  value: lastCapturedVitals.stress,
                })}
              </div>
            ) : null}
            {Number.isFinite(lastCapturedVitals?.wellness) ? (
              <div>
                {tDashboard("vitals_result_wellness", {
                  value: lastCapturedVitals.wellness,
                })}
              </div>
            ) : null}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLastCapturedVitals(null)} autoFocus>
            {tDashboard("done")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={snackbarAnchor}
      >
        <Alert
          closeText={tSts("close")}
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {renderInsightFrame()}
    </div>
  );
};

export default PersonDashboard;
